'use client'

// Súper online PR 2b (Tarea 2b.3): stock EN VIVO de lo retail de la tienda online.
//
// La carta sale de un cache por slug y el carrito vive en el navegador: ninguno sabe
// cuánto queda. Este hook pregunta a POST /api/online-orders/retail-stock al abrir un
// producto por peso o con variantes (ItemModal) y al entrar al carrito con líneas
// retail (OrderSheet), y nunca lee el blob cacheado (`cache: 'no-store'`).
//
// Es una AYUDA para pintar «Agotado» / «Quedan N» (lib/retail/onlineStockView): la
// puerta sigue siendo /session, que rechaza con 409 out_of_stock antes de cobrar.
// Cualquier falla (sin red, 404 not_enabled, 429…) deja la vista en null = sin
// etiquetas, nunca «agotado» por error.
//
// Restaurantes: el hook solo pregunta si hay ids, y los ids salen solo de señales
// retail (peso, variante, o `is_retail` con la bandera `online_retail_v2`, que es la
// única que trae `weight_unit` en la fila). Un restaurante no hace ninguna consulta.

import { useEffect, useState } from 'react'
import type { RetailStockItem } from '@/app/api/online-orders/retail-stock/route'
import { stockBadge, type StockBadge } from '@/lib/retail/onlineStockView'

/** Stock vivo por `item_id`, tal como lo devuelve la ruta. */
export type RetailStockView = ReadonlyMap<string, RetailStockItem>

/** La ruta topa en 50 ids (el pedido online topa en 50 líneas). */
const MAX_IDS = 50
/** Una ayuda no puede colgar el modal ni el carrito. */
const TIMEOUT_MS = 4000

export function indexRetailStock(items: readonly RetailStockItem[]): RetailStockView {
  return new Map(items.map((i) => [i.item_id, i]))
}

/**
 * Pide el stock vivo. null ante cualquier cosa que no sea una lista completa a
 * tiempo. `fetchFn` y `timeoutMs` son inyectables solo para tests.
 */
export async function fetchRetailStock(
  locationId: string | null | undefined,
  itemIds: readonly string[],
  fetchFn: typeof fetch = fetch,
  timeoutMs: number = TIMEOUT_MS,
): Promise<RetailStockView | null> {
  const ids = [...new Set(itemIds)].slice(0, MAX_IDS)
  if (!locationId || ids.length === 0) return null
  let res: Response
  try {
    res = await fetchFn('/api/online-orders/retail-stock', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ locationId, itemIds: ids }),
      cache: 'no-store',
      signal: AbortSignal.timeout(timeoutMs),
    })
  } catch {
    return null
  }
  if (!res.ok) return null
  const data = (await res.json().catch(() => null)) as { items?: unknown } | null
  if (!data || !Array.isArray(data.items)) return null
  return indexRetailStock(data.items as RetailStockItem[])
}

/**
 * Lo que queda para una línea: con variante, el número de ESA variante (0 si está
 * desactivada); sin variante, el del producto. null = no controla stock o sin dato.
 */
function availableFor(view: RetailStockView | null, itemId: string, variantId?: string | null): number | null {
  const item = view?.get(itemId)
  if (!item || !item.track_stock) return null
  if (variantId) {
    const v = item.variants.find((x) => x.id === variantId)
    if (!v) return null
    return v.is_active ? v.available : 0
  }
  return item.available
}

/**
 * Etiqueta de una línea (o del producto, sin cantidad): «Agotado», «Quedan N» si
 * quedan pocas o si la línea pide más de lo que hay, o nada.
 */
export function retailStockBadge(
  view: RetailStockView | null,
  line: { itemId: string; variantId?: string | null; weightMilli?: number | null; quantity?: number },
): StockBadge {
  const available = availableFor(view, line.itemId, line.variantId)
  if (available === null) return null
  const item = view!.get(line.itemId)!
  const variant = line.variantId ? item.variants.find((v) => v.id === line.variantId) : undefined
  const badge = stockBadge(available, variant?.low_stock_threshold ?? item.low_stock_threshold)
  if (badge) return badge
  const requested = line.weightMilli != null ? line.weightMilli / 1000 : line.quantity
  if (requested != null && requested > available) return { kind: 'low', left: available }
  return null
}

/** Para el selector de variantes: cuáles están agotadas y a cuáles les quedan pocas. */
export function retailVariantStock(
  view: RetailStockView | null,
  itemId: string,
): { soldOutVariantIds: Set<string>; stockLeftByVariant: Record<string, number> } {
  const soldOutVariantIds = new Set<string>()
  const stockLeftByVariant: Record<string, number> = {}
  const item = view?.get(itemId)
  if (!item || !item.track_stock) return { soldOutVariantIds, stockLeftByVariant }
  for (const v of item.variants) {
    const badge = retailStockBadge(view, { itemId, variantId: v.id })
    if (badge?.kind === 'sold_out') soldOutVariantIds.add(v.id)
    else if (badge?.kind === 'low') stockLeftByVariant[v.id] = badge.left
  }
  return { soldOutVariantIds, stockLeftByVariant }
}

/**
 * Qué productos del carrito preguntan: líneas por peso o con variante, y productos
 * `is_retail` que llegaron con la bandera (la fila trae la clave `weight_unit`).
 * Un plato nunca: sin señal retail la lista sale vacía y no hay consulta.
 */
export function retailStockItemIds(
  cart: ReadonlyArray<{ itemId: string; weightMilli?: number | null; variantId?: string | null }>,
  menuItems: ReadonlyArray<{ id: string; is_retail?: boolean | null; weight_unit?: string | null }>,
): string[] {
  const retail = new Set(menuItems.filter((m) => m.is_retail === true && m.weight_unit !== undefined).map((m) => m.id))
  const ids = cart
    .filter((l) => l.weightMilli != null || l.variantId != null || retail.has(l.itemId))
    .map((l) => l.itemId)
  return [...new Set(ids)]
}

/**
 * Stock vivo de `itemIds` mientras `active`. Vuelve a preguntar cada vez que se
 * activa (abrir el producto, entrar al carrito) o cambian los ids. null mientras
 * no hay respuesta, o si no hay nada que preguntar.
 */
export function useRetailStock(
  locationId: string | null | undefined,
  itemIds: readonly string[],
  active: boolean,
): RetailStockView | null {
  const key = active && locationId ? [...new Set(itemIds)].sort().join(',') : ''
  const [state, setState] = useState<{ key: string; view: RetailStockView | null }>({ key: '', view: null })
  useEffect(() => {
    if (!key) return
    let cancelled = false
    void fetchRetailStock(locationId, key.split(',')).then((view) => {
      if (!cancelled) setState({ key, view })
    })
    return () => {
      cancelled = true
    }
  }, [key, locationId])
  return key && state.key === key ? state.view : null
}
